import { useState, useEffect } from "react";
import "./App.css";

const MAX_TITLE_LENGTH = 200;
const MAX_CONTENT_LENGTH = 5000;

// 前端输入清理（防止绕过后端直接注入）
function sanitizeInput(str) {
  return str
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");
}

function hasDangerousContent(str) {
  const patterns = [
    /<script[^>]*>/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
  ];
  return patterns.some((p) => p.test(str));
}

function App() {
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = async () => {
    try {
      const res = await fetch("/api/posts");
      if (!res.ok) throw new Error("Failed to fetch posts");
      const data = await res.json();
      setPosts(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();

    if (!trimmedTitle || !trimmedContent) {
      setError("Title and content are required");
      return;
    }

    if (trimmedTitle.length > MAX_TITLE_LENGTH) {
      setError(`Title must be under ${MAX_TITLE_LENGTH} characters`);
      return;
    }

    if (trimmedContent.length > MAX_CONTENT_LENGTH) {
      setError(`Content must be under ${MAX_CONTENT_LENGTH} characters`);
      return;
    }

    if (hasDangerousContent(trimmedTitle) || hasDangerousContent(trimmedContent)) {
      setError("Input contains forbidden content");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: sanitizeInput(trimmedTitle),
          content: sanitizeInput(trimmedContent),
        }),
      });
      if (res.status === 429) {
        setError("Too many requests, please try again later");
        return;
      }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to create post");
      }
      setTitle("");
      setContent("");
      fetchPosts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1>Tritriniu</h1>
        <p>A simple website</p>
      </header>

      <main className="main">
        <section className="new-post">
          <h2>Create a Post</h2>
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <input
                type="text"
                placeholder="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={MAX_TITLE_LENGTH}
                required
              />
              <span className="char-count">
                {title.length}/{MAX_TITLE_LENGTH}
              </span>
            </div>
            <div className="input-group">
              <textarea
                placeholder="Content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={MAX_CONTENT_LENGTH}
                rows={4}
                required
              />
              <span className="char-count">
                {content.length}/{MAX_CONTENT_LENGTH}
              </span>
            </div>
            <button type="submit" disabled={submitting}>
              {submitting ? "Publishing..." : "Publish"}
            </button>
          </form>
        </section>

        <section className="posts">
          <h2>Posts</h2>
          {loading && <p>Loading...</p>}
          {error && <p className="error">{error}</p>}
          {posts.map((post) => (
            <article key={post.id} className="post-card">
              <h3>{post.title}</h3>
              <time>{post.date}</time>
              <p>{post.content}</p>
            </article>
          ))}
          {!loading && posts.length === 0 && <p>No posts yet.</p>}
        </section>
      </main>

      <footer className="footer">
        <p>&copy; 2026 Tritriniu. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;
